import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

//Core Components
import Loader from "../../../core/common/loader";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import DeleteModal from "../../../core/modals/deleteModal";
import CommonSelect from "../../../core/common/commonSelect";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Table from "../../../core/common/dataTable";

// 🛠️ Helpers
import api from "../../../services/api";
import { notify } from "../../../services/toast.config";
import { exportToExcel, exportToPDF } from "../../../utils/exportData";
import { truncateCompanyText } from "../../../helper/company_helper";
import { humanReadableDate } from "../../../helper/dateTimeHelper.js";
import { all_routes } from "../../router/all_routes";
import { toProperCase } from "../../../helper/company_helper";

// Assigned asset components
import AssignNewAssetModal from "./components/assignAsset";
import EditAssignedAssetModal from "./components/editAssignedAsset";
import AssignAssetFilter from "./components/assignAssetFilter";
import AssignedAssetGrid from "./assigned-asset-grid"; // Updated import name

import AssetHistoryModal from "./components/assetHistoryModal";
import MarkAssetLostModal from "./components/markAssetLostModal";

// Types and constants
const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const EMPTY_COMPANY = {
  company_name: "Select Company",
  company_id: "",
  company_type: "Type",
};

const AssignAsset = () => {
  // 🔹 Local Storage || Redux store
  const signature = localStorage.getItem("signature");
  const [searchParams, setSearchParams] = useSearchParams();

  // Get view mode from query parameter, default to 'grid'
  const viewMode = searchParams.get("view") || "list";
  const isGridView = viewMode === "grid";

  // 🔹 State: UI
  const [isLoading, setIsLoading] = useState(false);

  // 🔹 State: Data
  const [formDataCompanyChoose, setFormDataCompanyChoose] =
    useState(EMPTY_COMPANY);
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [assetDepartmentOptions, setAssetDepartmentOptions] = useState<any[]>(
    []
  );
  const [assetCategoryOptions, setAssetCategoryOptions] = useState<any[]>([]);
  const [assetLocationOptions, setAssetLocationOptions] = useState<any[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);
  const [assetProductOptions, setAssetProductsOptions] = useState<any[]>([]);
  const [selectedAssetEdit, setSelectedAssetEdit] = useState<any | null>(null);
  const [selectedHistoryAsset, setSelectedHistoryAsset] = useState<any | null>(
    null
  );
  const [selectedLostAsset, setSelectedLostAsset] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // 🔹 State: Modal Actions
  const [deleteRecord, setDeleteRecord] = useState<{ id: number } | null>(null);

  // ⚙️ Derived Data
  const companyOptions = companies.map((company) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));
  const statusOptions = [...STATUS_OPTIONS];

  // Handler to switch views
  const handleViewChange = (newView: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", newView);
    setSearchParams(params);
  };

  // 🧩 Handlers: Form Inputs
  const handleCompanySelection = (selectedOption: any) => {
    if (!selectedOption) return;

    const newFormData = {
      company_id: selectedOption.value,
      company_name: selectedOption.label.split("-")[0]?.trim() || "",
      company_type: selectedOption.label.split("-")[1]?.trim() || "",
    };

    localStorage.setItem("selectedAssetCompany", JSON.stringify(newFormData));

    setFormDataCompanyChoose((prev: any) => ({
      ...prev,
      ...newFormData,
    }));
  };

  const handleAssignedAssetEdit = (record: any) => {
    try {
      setSelectedAssetEdit(record);
    } catch (e: any) {
      notify.error(e?.message || "Error occured while editing!");
    }
  };

  // 🗑️ Delete: Assets
  const handleDelete = async () => {
    if (deleteRecord) {
      try {
        try {
          setIsLoading(true);
          const response = await api.post("assetService/deleteAssingedAsset", {
            signature,
            id: deleteRecord?.id,
            company_id: formDataCompanyChoose?.company_id,
            company_type: formDataCompanyChoose?.company_type,
          });
          notify.success(response.data.message[0].msg);
        } catch (error: any) {
          notify.error(
            error?.response?.data?.message?.[0]?.msg || "An error occurred."
          );
        } finally {
          await updateUI();
          setIsLoading(false);
        }
      } catch (error) {
        notify.error("Failed to delete item.");
      }
      setDeleteRecord(null);
    }
  };

  const handleBulkDelete = async (keys: any[]) => {
    if (keys) {
      try {
        try {
          setIsLoading(true);
          const response = await api.post(
            "assetService/bulkDeleteAssingedAsset",
            {
              signature,
              ids: keys,
              company_id: formDataCompanyChoose?.company_id,
              company_type: formDataCompanyChoose?.company_type,
            }
          );
          notify.success(response.data.message[0].msg);
        } catch (error: any) {
          notify.error(error?.data?.message?.[0]?.msg || "An error occurred.");
        } finally {
          await updateUI();
          setIsLoading(false);
        }
      } catch (error) {
        notify.error("Failed to delete item.");
      }
    }
  };

  // 🔄 Fetch Data
  const getCompaniesAndDepartments = async () => {
    try {
      const [getCompaniesResponse, getDepartmentResponse] =
        await Promise.allSettled([
          api.post("userService/getAllCompanyList", { signature }),
          api.post("userService/getDepartment", { signature }),
        ]);

      const companiesData =
        getCompaniesResponse.status === "fulfilled"
          ? getCompaniesResponse.value.data?.data?.data || []
          : [];

      const departments =
        getDepartmentResponse.status === "fulfilled"
          ? getDepartmentResponse.value.data?.data?.data || []
          : [];

      const activeDepartments = departments?.filter(
        (department: any) => department?.Status?.toLowerCase() == "active"
      );
      setCompanies(companiesData);
      setAssetDepartmentOptions(activeDepartments);
    } catch (error) {
      notify.error("Failed to load data.");
    }
  };

  const fetchAllData = async () => {
    if (formDataCompanyChoose.company_id !== "") {
      try {
        const [
          getEmployeesResponse,
          getProductsResponse,
          getCategoryResponse,
          getLocationsResponse,
          getAssetsResponse,
        ] = await Promise.allSettled([
          api.post("userService/getAllEmployeesListDataByCompanyId", {
            signature,
            companyId: formDataCompanyChoose.company_id,
            companyType: formDataCompanyChoose.company_type,
          }),
          api.post("userService/getAllUnAssignedProducts", {
            signature,
            company_id: formDataCompanyChoose.company_id,
            company_type: formDataCompanyChoose.company_type,
          }),
          api.post("userService/getCategory", {
            signature,
            company_id: formDataCompanyChoose.company_id,
            company_type: formDataCompanyChoose.company_type,
          }),
          api.post("userService/getLocation", {
            signature,
            company_id: formDataCompanyChoose.company_id,
            company_type: formDataCompanyChoose.company_type,
          }),
          api.post("assetService/getListAssignedAssets", {
            signature,
            company_id: formDataCompanyChoose.company_id,
            company_type: formDataCompanyChoose.company_type,
          }),
        ]);

        const employees =
          getEmployeesResponse.status === "fulfilled"
            ? getEmployeesResponse.value.data?.data?.data?.allUsers || []
            : [];

        const getProducts =
          getProductsResponse.status === "fulfilled"
            ? getProductsResponse.value.data?.data?.data || []
            : [];
        const getCategory =
          getCategoryResponse.status === "fulfilled"
            ? getCategoryResponse.value.data?.data?.data || []
            : [];
        const getLocation =
          getLocationsResponse.status === "fulfilled"
            ? getLocationsResponse.value.data?.data?.data || []
            : [];

        const getAssets =
          getAssetsResponse.status === "fulfilled"
            ? getAssetsResponse.value.data?.data?.data || []
            : [];

        const activeProducts = getProducts?.filter(
          (product: any) => product?.status?.toLowerCase() == "active"
        );

        const activeLocations = getLocation?.filter(
          (location: any) => location?.status?.toLowerCase() == "active"
        );
        setEmployeeOptions(employees);
        setData(getAssets);
        setFilteredData(getAssets);
        setAssetProductsOptions(activeProducts);
        setAssetCategoryOptions(getCategory);
        setAssetLocationOptions(activeLocations);
      } catch (error) {
        notify.error("Failed to fetch data.");
      }
    }
  };

  const updateUI = async () => {
    setIsLoading(true);
    const startTime = Date.now();

    try {
      await fetchAllData();
    } catch (error: any) {
      notify.error(error?.message || "Error Occured!");
    } finally {
      const elapsed = Date.now() - (startTime + 300);
      const remaining = 300 - elapsed;

      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    }
  };

  //Use Effects HOOK
  useEffect(() => {
    const savedView = localStorage.getItem("assignAssetView") || "list";
    if (!searchParams.get("view")) {
      handleViewChange(savedView);
    }
    getCompaniesAndDepartments();
  }, []);

  useEffect(() => {
    if (companies.length) {
      const storedCompany = localStorage.getItem("selectedAssetCompany");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        handleCompanySelection({
          value: parsed.company_id,
          label: `${parsed.company_name} - ${parsed.company_type}`,
        });
      } else {
        handleCompanySelection({
          value: companies[0].id.toString(),
          label: companies[0].name + " - " + companies[0].type,
        });
      }
    }
  }, [companies]);

  useEffect(() => {
    if (formDataCompanyChoose.company_id) {
      updateUI();
    }
  }, [formDataCompanyChoose.company_id, formDataCompanyChoose.company_type]);

  // Save view preference to localStorage
  useEffect(() => {
    if (viewMode) {
      localStorage.setItem("assignAssetView", viewMode);
    }
  }, [viewMode]);

  // 📤 Export Handlers
  const cols = [
    "Product Name",
    "Model",
    "Serial Number",
    "Assigned To",
    "Assigned By",
    "Assigned Date",
    "Status",
  ];
  const keys = [
    "product_name",
    "model",
    "serial_no",
    "assigned_to_name",
    "assigned_by_name",
    "assigned_date",
    "status",
  ];

  const handleExportPDF = () => {
    exportToPDF({
      data: filteredData,
      headers: cols,
      keys: keys,
      fileName: "assigned-assets-data",
    });
  };

  const handleExportExcel = () => {
    exportToExcel({
      data: filteredData,
      headers: cols,
      keys: keys,
      fileName: "assigned-assets-data",
    });
  };

  // Table Columns (keeping your existing columns)
  const columns = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.ProductDetails?.image?.url ||
                record?.ProductDetails?.image?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.ProductDetails?.image?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-12 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.ProductDetails?.model)}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.product_name || "").localeCompare(
          b?.product_name || "",
          "en",
          { sensitivity: "base", numeric: true }
        );

        return nameCompare !== 0
          ? nameCompare
          : (a?.product_model || "").localeCompare(
              b?.product_model || "",
              "en",
              { sensitivity: "base", numeric: true }
            );
      },
    },
    {
      title: "Serial Number",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-12 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2 table-text-wrap">
          {text.toUpperCase()}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Assigned By",
      dataIndex: "assigned_by_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-sm avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.assigned_by_logo || record?.assigned_by_logo === "null"
                  ? "assets/img/users/user.png"
                  : record?.assigned_by_logo
              }
              alt="img"
            />
          </li>

          <div>
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>

            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.assigned_by_designation || "NA")}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_by_name || "").localeCompare(b?.assigned_by_name || ""),
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_to_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(text)}
            </h6>
            {record.assigned_to_type == "user" && (
              <span className="fs-12 fw-normal table-text-wrap">
                {toProperCase(record?.assigned_to_designation || "NA")}
              </span>
            )}
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.assigned_to_name || "").localeCompare(b?.assigned_to_name || ""),
    },
    {
      title: "Assigned Date",
      dataIndex: "assigned_date",
      sorter: (a: any, b: any) =>
        new Date(a?.assigned_date).getTime() -
        new Date(b?.assigned_date).getTime(),
      render: (text: string) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-soft-success">
          {humanReadableDate(text)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: any) => (
        <span
          className={`badge d-inline-flex align-items-center badge-xs ${
            text === "Active"
              ? "badge-success"
              : text === "Inactive"
              ? "badge-danger"
              : "badge-warning"
          }`}
        >
          <i className="ti ti-point-filled me-1"></i>
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a?.status?.length - b?.status?.length,
    },
    {
      title: "",
      dataIndex: "actions",
      render: (text: any, record: any) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            className="me-2"
            title="Show Asset History"
            data-bs-toggle="modal"
            data-bs-target="#asset-history-modal"
            data-inert={true}
            onClick={() => setSelectedHistoryAsset(record)}
          >
            <i className="ti ti-eye" />
          </Link>
          {record.status !== "Lost" && (
            <Link
              title="Edit Assigned Asset"
              to="#"
              className="me-2"
              data-bs-toggle="modal"
              data-inert={true}
              data-bs-target="#edit_asset_modal"
              onClick={() => handleAssignedAssetEdit(record)}
            >
              <i className="ti ti-edit" />
            </Link>
          )}
          <Link
            title={record.status === "Lost" ? "Recover Asset" : "Mark as Lost"}
            to="#"
            className="me-2"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#mark_asset_lost_modal"
            onClick={() => setSelectedLostAsset(record)}
          >
            <i
              className={`ti ${
                record.status === "Lost"
                  ? "ti-refresh-dot text-success"
                  : "ti-alert-triangle text-warning"
              }`}
            />
          </Link>
          <Link
            title="Delete Assigned Assset"
            to="#"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_assset_modal"
            onClick={() => setDeleteRecord(record)}
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading && <Loader />}

      {/* Headers Section  */}
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Assign Assets</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Assign Assets {isGridView ? "(Grid View)" : "(List View)"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* View Toggle - Updated to use buttons */}
              <div className="me-2 mb-2">
                <div className="d-flex align-items-center border bg-white rounded p-1 me-2 icon-list">
                  <button
                    onClick={() => handleViewChange("list")}
                    className={`btn btn-icon btn-sm ${
                      !isGridView ? "active bg-primary text-white" : ""
                    } me-1`}
                    title="List View"
                  >
                    <i className="ti ti-list-tree" />
                  </button>
                  <button
                    onClick={() => handleViewChange("grid")}
                    className={`btn btn-icon btn-sm ${
                      isGridView ? "active bg-primary text-white" : ""
                    } me-1`}
                    title="Grid View"
                  >
                    <i className="ti ti-layout-grid" />
                  </button>
                </div>
              </div>

              {/* Company selection  */}
              <div className="dropdown me-2 mb-2">
                <CommonSelect
                  className="select"
                  options={companyOptions}
                  defaultValue={{
                    value: formDataCompanyChoose.company_id,
                    label:
                      truncateCompanyText(
                        formDataCompanyChoose.company_name,
                        20
                      ) +
                      "-" +
                      formDataCompanyChoose.company_type,
                  }}
                  onChange={handleCompanySelection}
                />
              </div>

              <div className="dropdown me-2 mb-2" style={{ zIndex: 1000 }}>
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-file-export me-1" />
                  Export
                </Link>
                <ul className="dropdown-menu p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={handleExportPDF}
                    >
                      <i className="ti ti-file-type-pdf me-1" />
                      Export as PDF
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={handleExportExcel}
                    >
                      <i className="ti ti-file-type-xls me-1" />
                      Export as Excel
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="mb-2 me-2">
                <Link
                  to="#"
                  data-inert={true}
                  data-bs-toggle="modal"
                  data-bs-target="#assign_asset"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-user-plus me-1" />
                  Assign Asset
                </Link>
              </div>
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Content rendering - Grid view shows by default */}
          {isGridView ? (
            <div>
              <div className="card" style={{ zIndex: 99 }}>
                <div className=" card-header d-flex justify-content-between align-items-center flex-wrap row-gap-3">
                  <h5>Assigned Assets Grid</h5>

                  {/* Search Box */}

                  <div className="input-group" style={{ maxWidth: "300px" }}>
                    <span className="input-group-text">
                      <i className="ti ti-search" />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search assets..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchQuery("")}
                      >
                        <i className="ti ti-x" />
                      </button>
                    )}
                  </div>

                  <div className="w-100">
                    <AssignAssetFilter
                      data={data}
                      setFilteredData={setFilteredData}
                      assetCategoryOptions={assetCategoryOptions}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                    />
                  </div>
                </div>
              </div>
              <div className="p-0">
                <AssignedAssetGrid
                  setSelectedHistoryAsset={setSelectedHistoryAsset}
                  filteredData={filteredData}
                  setDeleteRecord={setDeleteRecord}
                  handleAssignedAssetEdit={handleAssignedAssetEdit}
                  setSelectedLostAsset={setSelectedLostAsset}
                />
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h5>Assigned Assets List</h5>
                <AssignAssetFilter
                  data={data}
                  setFilteredData={setFilteredData}
                  assetCategoryOptions={assetCategoryOptions}
                  setSearchQuery={setSearchQuery}
                />
              </div>
              <div className="card-body p-0">
                <Table
                  dataSource={filteredData}
                  columns={columns}
                  Selection={true}
                  onBulkDelete={handleBulkDelete}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      {/* Modals */}
      <AssignNewAssetModal
        companyOptions={companyOptions}
        formDataCompanyChoose={formDataCompanyChoose}
        assetCategoryOptions={assetCategoryOptions}
        assetDepartmentOptions={assetDepartmentOptions}
        assetLocationOptions={assetLocationOptions}
        employeeOptions={employeeOptions}
        assetProductOptions={assetProductOptions}
        statusOptions={statusOptions}
        updateUI={updateUI}
      />

      <EditAssignedAssetModal
        assetCategoryOptions={assetCategoryOptions}
        assetDepartmentOptions={assetDepartmentOptions}
        assetLocationOptions={assetLocationOptions}
        employeeOptions={employeeOptions}
        statusOptions={statusOptions}
        updateUI={updateUI}
        selectedAssetEdit={selectedAssetEdit}
        setSelectedAssetEdit={setSelectedAssetEdit}
        formDataCompanyChoose={formDataCompanyChoose}
      />

      <DeleteModal
        modalId="delete_assset_modal"
        onDelete={handleDelete}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />

      <AssetHistoryModal
        assetId={selectedHistoryAsset?.id}
        assetName={selectedHistoryAsset?.product_name}
        selectedHistoryAsset={selectedHistoryAsset}
      />

      <MarkAssetLostModal
        selectedLostAsset={selectedLostAsset}
        setSelectedLostAsset={setSelectedLostAsset}
        formDataCompanyChoose={formDataCompanyChoose}
        updateUI={updateUI}
      />
    </>
  );
};

export default AssignAsset;
